/**
 * SBT OTA firmware upgrade for earphone noise-cancel / Bluetooth targets.
 * Flow and packet rules are aligned with oldcode/js/sbtOtaUpgrade.js.
 */

import type { UpgradeCallbacks } from './firmware-types'
import { sleep } from './firmware-device'

const HEADER_AA = 0xaa
const HEADER_55 = 0x55
const POLY_CRC16 = 0x1021

const CMD = {
  QUERY_STATUS: 0x80,
  DATA_REQUEST: 0x81,
  UPGRADE_RESULT: 0x82,
  FILE_INFO: 0x88,
} as const

const DEVICE_STATUS = {
  READY: 0,
  LOW_CURRENT: 1,
  LOW_POWER: 2,
  BUSY: 3,
} as const

const DEFAULT_CONFIG = {
  queryTimeoutMs: 6000,
  queryRetries: 3,
  queryRetryBaseMs: 200,
  loopTimeoutMs: 60000,
  sendRetries: 3,
  sendRetryDelayMs: 100,
  chunkSize: 63,
  preChunkDelayMs: 6,
  postChunkDelayMs: 4,
  postReplyDelayMs: 5,
  verifyCrc: true,
  maxRequestLength: 1024,
  padLastChunkTo63: false,
  btPreChunkDelayMs: 6,
  btPostChunkDelayMs: 4,
  btPadLastChunkTo63: true,
  btPost88DelayMs: 30,
}

type SbtConfig = typeof DEFAULT_CONFIG

let device: HIDDevice | null = null
let firmwareBuffer: Uint8Array | null = null
let upgradeTarget = 0
let config: SbtConfig = { ...DEFAULT_CONFIG }
let isRunning = false
let lastReportedProgress = 0
let useReportId49 = true
let isBluetoothUpgrade = false
let ioDevices: HIDDevice[] = []

let onProgressCallback: UpgradeCallbacks['onProgress'] = null
let onCompleteCallback: UpgradeCallbacks['onComplete'] = null
let onErrorCallback: UpgradeCallbacks['onError'] = null
let onLogCallback: UpgradeCallbacks['onLog'] = null

let inputReportHandler: ((e: HIDInputReportEvent) => void) | null = null
let pendingResolve: ((v: Uint8Array | null) => void) | null = null
let packetQueue: Uint8Array[] = []

function log(message: string, level: string = 'info'): void {
  onLogCallback?.(message, level)
}

function getCrc1byte(lastcrc: number, byteVal: number): number {
  let crc = lastcrc
  for (let i = 0; i < 8; i++) {
    if (((byteVal << i) ^ (crc >> 8)) & 0x80) {
      crc = ((crc << 1) ^ POLY_CRC16) & 0xffff
    } else {
      crc = (crc << 1) & 0xffff
    }
  }
  return crc
}

function calcCrc(init: number, buf: Uint8Array): number {
  let val = init
  for (let i = 0; i < buf.length; i++) val = getCrc1byte(val, buf[i])
  return val
}

function isValidHeader(data: Uint8Array): boolean {
  return data.length >= 2 && data[0] === HEADER_AA && data[1] === HEADER_55
}

function buildQuery80(target: number): Uint8Array {
  const pkt = new Uint8Array(8)
  pkt[0] = HEADER_AA
  pkt[1] = HEADER_55
  pkt[4] = CMD.QUERY_STATUS
  pkt[5] = 0x01
  pkt[6] = 0x00
  pkt[7] = target
  const crc = calcCrc(0xffff, pkt.subarray(4, 8))
  pkt[2] = crc & 0xff
  pkt[3] = (crc >> 8) & 0xff
  return pkt
}

function buildData81(addr: number, data: Uint8Array): Uint8Array {
  const len = data.length
  const pkt = new Uint8Array(13 + len)
  pkt[0] = HEADER_AA
  pkt[1] = HEADER_55
  pkt[4] = CMD.DATA_REQUEST
  pkt[5] = (len + 6) & 0xff
  pkt[6] = ((len + 6) >> 8) & 0xff
  pkt[7] = addr & 0xff
  pkt[8] = (addr >> 8) & 0xff
  pkt[9] = (addr >> 16) & 0xff
  pkt[10] = (addr >> 24) & 0xff
  pkt[11] = len & 0xff
  pkt[12] = (len >> 8) & 0xff
  pkt.set(data, 13)
  const crc = calcCrc(0xffff, pkt.subarray(4, 13 + len))
  pkt[2] = crc & 0xff
  pkt[3] = (crc >> 8) & 0xff
  return pkt
}

function buildReply88(fileSize: number): Uint8Array {
  const pkt = new Uint8Array(14)
  pkt[0] = HEADER_AA
  pkt[1] = HEADER_55
  pkt[4] = CMD.FILE_INFO
  pkt[5] = 0x07
  pkt[6] = 0x00
  pkt[7] = fileSize & 0xff
  pkt[8] = (fileSize >> 8) & 0xff
  pkt[9] = (fileSize >> 16) & 0xff
  pkt[10] = (fileSize >> 24) & 0xff
  pkt[11] = 0x01
  pkt[12] = 0x00
  pkt[13] = 0x00
  const crc = calcCrc(0xffff, pkt.subarray(4, 14))
  pkt[2] = crc & 0xff
  pkt[3] = (crc >> 8) & 0xff
  return pkt
}

function parseStatus80(data: Uint8Array): number | null {
  if (data.length < 8 || !isValidHeader(data) || data[4] !== CMD.QUERY_STATUS) return null
  if (config.verifyCrc) {
    const receivedCrc = data[2] | (data[3] << 8)
    const calculatedCrc = calcCrc(0xffff, data.subarray(4, 8))
    if (receivedCrc !== calculatedCrc) log('0x80 CRC mismatch', 'warn')
  }
  return data[7]
}

function parseRequest81(data: Uint8Array) {
  if (data.length < 14 || !isValidHeader(data) || data[4] !== CMD.DATA_REQUEST) return null
  const addr = (data[7] & 0xff) | ((data[8] & 0xff) << 8) | ((data[9] & 0xff) << 16) | ((data[10] & 0xff) << 24)
  let length = (data[11] & 0xff) | ((data[12] & 0xff) << 8)
  const reportedProgress = data[13] ?? 0
  if (length === 0 || length > config.maxRequestLength) length = 512
  return { addr, length, reportedProgress }
}

function parseRequest88(data: Uint8Array) {
  if (data.length < 8 || !isValidHeader(data) || data[4] !== CMD.FILE_INFO) return null
  const dataLen = (data[5] & 0xff) | ((data[6] & 0xff) << 8)
  if (dataLen <= 1 || data.length < 13) return { addr: 0, reqLen: 0, simple: true }
  const addr = (data[7] & 0xff) | ((data[8] & 0xff) << 8) | ((data[9] & 0xff) << 16) | ((data[10] & 0xff) << 24)
  const reqLen = (data[11] & 0xff) | ((data[12] & 0xff) << 8)
  return { addr, reqLen, simple: false }
}

function parseResult82(data: Uint8Array) {
  if (data.length < 8 || !isValidHeader(data) || data[4] !== CMD.UPGRADE_RESULT) return null
  const result = data[7]
  return { errorCode: result & 0x0f, partialDone: (result & 0xf0) !== 0 }
}

function calculateTransferProgress(addr: number, length: number, reportedProgress: number): number {
  if (Number.isFinite(reportedProgress) && reportedProgress > 0 && reportedProgress <= 100) {
    return Math.floor(reportedProgress)
  }
  const totalSize = firmwareBuffer?.length ?? 0
  if (!totalSize) return 0
  const transferredEnd = Math.max(0, Math.min(addr + length, totalSize))
  const percent = Math.floor((transferredEnd / totalSize) * 100)
  return transferredEnd >= totalSize ? 99 : Math.max(0, Math.min(percent, 99))
}

async function sendReportWithRetry(reportId: number, data: Uint8Array): Promise<void> {
  if (!device?.opened) throw new Error('Device is not connected')
  let lastError: unknown
  for (let i = 0; i < config.sendRetries; i++) {
    try {
      await device.sendReport(reportId, data)
      return
    } catch (error) {
      lastError = error
      await sleep(config.sendRetryDelayMs * (i + 1))
    }
  }
  throw lastError
}

async function sendInChunks(packet: Uint8Array, reportId = 49): Promise<void> {
  const count = Math.floor(packet.length / config.chunkSize)
  const last = packet.length % config.chunkSize
  const preDelay = isBluetoothUpgrade ? config.btPreChunkDelayMs : config.preChunkDelayMs
  const postDelay = isBluetoothUpgrade ? config.btPostChunkDelayMs : config.postChunkDelayMs
  const shouldPadLast = isBluetoothUpgrade ? config.btPadLastChunkTo63 : config.padLastChunkTo63

  for (let i = 0; i < count; i++) {
    await sleep(preDelay)
    await sendReportWithRetry(reportId, packet.slice(i * config.chunkSize, (i + 1) * config.chunkSize))
    await sleep(postDelay)
  }

  if (last > 0) {
    await sleep(preDelay)
    let lastChunk: Uint8Array
    if (shouldPadLast) {
      lastChunk = new Uint8Array(config.chunkSize)
      lastChunk.set(packet.slice(count * config.chunkSize))
    } else {
      lastChunk = packet.slice(count * config.chunkSize)
    }
    await sendReportWithRetry(reportId, lastChunk)
    await sleep(postDelay)
  }
}

function waitForInput(timeoutMs: number): Promise<Uint8Array | null> {
  if (packetQueue.length > 0) return Promise.resolve(packetQueue.shift()!)
  return new Promise(resolve => {
    pendingResolve = resolve
    const timer = setTimeout(() => {
      if (pendingResolve === resolve) {
        pendingResolve = null
        resolve(null)
      }
    }, timeoutMs)
    void timer
  })
}

function onInputReport(event: HIDInputReportEvent): void {
  const data = new Uint8Array(event.data.buffer)
  packetQueue.push(data)
  if (pendingResolve && packetQueue.length > 0) {
    const resolve = pendingResolve
    pendingResolve = null
    resolve(packetQueue.shift()!)
  }
}

function updateProgress(percent: number, message: string): void {
  if (percent < 100) percent = Math.max(percent, lastReportedProgress)
  lastReportedProgress = percent
  onProgressCallback?.(percent, message)
}

function buildResultValueByte(resultCode: number, hasPendingDevice: boolean): number {
  const code = Number(resultCode) & 0x0f
  const pendingNibble = hasPendingDevice ? 0x10 : 0x00
  return (pendingNibble | code) & 0xff
}

function inferUpdateFailedReasonByte(error: unknown): number {
  const msg = String(error instanceof Error ? error.message : error || '')
  const hasPendingDevice = !!(device && device.opened)
  if (/crc|校验/i.test(msg)) return buildResultValueByte(0x02, hasPendingDevice)
  if (/version|版本|low|过低/i.test(msg)) return buildResultValueByte(0x01, hasPendingDevice)
  return buildResultValueByte(0x01, hasPendingDevice)
}

async function sendUpdateFailedNotice(resultValueByte = 0x11): Promise<void> {
  if (!device?.opened) return
  const data = new Uint8Array([0xaa, 0x55, 0x00, 0x00, 0x82, 0x01, 0x00, resultValueByte & 0xff])
  const crc = calcCrc(0xffff, data.subarray(4, 8))
  data[2] = crc & 0xff
  data[3] = (crc >> 8) & 0xff
  await device.sendReport(useReportId49 ? 49 : 0, data)
}

async function queryDeviceStatus(target: number): Promise<number> {
  while (packetQueue.length > 0 && !isValidHeader(packetQueue[0])) packetQueue.shift()

  for (let retry = 0; retry < config.queryRetries; retry++) {
    await sendReportWithRetry(49, buildQuery80(target))
    const deadline = Date.now() + config.queryTimeoutMs

    while (Date.now() < deadline) {
      const response = await waitForInput(Math.max(0, deadline - Date.now()))
      if (!response) break
      if (!isValidHeader(response)) continue

      const cmd = response[4]
      if (cmd === CMD.FILE_INFO || cmd === CMD.DATA_REQUEST || cmd === CMD.UPGRADE_RESULT) {
        packetQueue.unshift(response)
        return DEVICE_STATUS.READY
      }
      if (cmd !== CMD.QUERY_STATUS) continue

      const status = parseStatus80(response)
      if (status === null) continue
      if (status !== DEVICE_STATUS.READY) {
        const errMap: Record<number, string> = {
          1: 'Upgrade target current is low',
          2: 'Upgrade target battery is low',
          3: 'Device is busy',
        }
        throw new Error(errMap[status] || `Device status is not ready (${status})`)
      }
      return status
    }

    await sleep(config.queryRetryBaseMs * (retry + 1))
  }

  throw new Error('Device did not respond, please retry')
}

async function handleDataRequest(requestData: Uint8Array): Promise<number> {
  const req = parseRequest81(requestData)
  if (!req || !firmwareBuffer) throw new Error('Unable to parse data request packet')
  const { addr, length, reportedProgress } = req
  const totalSize = firmwareBuffer.length
  if (addr >= totalSize) return 0

  const realLen = Math.min(totalSize - addr, length)
  const chunk = new Uint8Array(length)
  chunk.set(firmwareBuffer.slice(addr, addr + realLen), 0)

  const progress = calculateTransferProgress(addr, realLen, reportedProgress)
  await sendInChunks(buildData81(addr, chunk), useReportId49 ? 49 : 0)
  return progress
}

async function performUpgrade(): Promise<void> {
  const pid = device?.productId ?? 0
  lastReportedProgress = 0

  if (pid >= 0x9527 && pid <= 0x9530) {
    await sleep(4000)
  }

  if (useReportId49) {
    updateProgress(0, 'Waiting for device initialization...')
    await sleep(3000)
  }

  updateProgress(0, 'Querying device status...')
  try {
    await queryDeviceStatus(upgradeTarget)
  } catch (error) {
    if (useReportId49) {
      await sleep(2000)
      try {
        await queryDeviceStatus(upgradeTarget)
      } catch {
        log('SBT status query failed twice, continue waiting for OTA requests', 'warn')
      }
    } else {
      throw error
    }
  }

  updateProgress(0, 'Start transferring firmware data...')
  while (isRunning) {
    const packet = await waitForInput(config.loopTimeoutMs)
    if (!packet) throw new Error(`Upgrade response timeout (${Math.floor(config.loopTimeoutMs / 1000)}s without device request)`)
    if (!isValidHeader(packet)) continue

    if (isBluetoothUpgrade && packet[0] === 0xaa && packet[1] === 0x53) continue

    const cmd = packet[4]
    if (cmd === CMD.DATA_REQUEST) {
      const progress = await handleDataRequest(packet)
      updateProgress(progress, `Transferring ${progress}%`)
      continue
    }

    if (cmd === CMD.FILE_INFO) {
      const req88 = parseRequest88(packet)
      if (!req88) continue
      await sendInChunks(buildReply88((firmwareBuffer?.length ?? 0) >>> 0), useReportId49 ? 49 : 0)
      continue
    }

    if (cmd === CMD.UPGRADE_RESULT) {
      const result = parseResult82(packet)
      if (!result) continue

      if (result.errorCode === 1) throw new Error('Upgrade failed: firmware version is too low')
      if (result.errorCode === 2) throw new Error('Upgrade failed: CRC check failed')
      if (result.errorCode !== 0) throw new Error(`Upgrade failed: unknown error code ${result.errorCode}`)

      if (result.partialDone) {
        updateProgress(100, 'Current device complete, waiting for next device...')
        continue
      }

      updateProgress(100, 'Upgrade complete!')
      onCompleteCallback?.()
      return
    }
  }
}

function cleanup(): void {
  if (inputReportHandler && ioDevices.length > 0) {
    for (const d of ioDevices) {
      try {
        d.removeEventListener('inputreport', inputReportHandler)
      } catch {
        /* ignore */
      }
    }
  } else if (device && inputReportHandler) {
    try {
      device.removeEventListener('inputreport', inputReportHandler)
    } catch {
      /* ignore */
    }
  }
  inputReportHandler = null
  pendingResolve = null
  packetQueue = []
  ioDevices = []
  isRunning = false
  isBluetoothUpgrade = false
}

function hasReportId(dev: HIDDevice, reportId: number): boolean {
  return (dev.collections || []).some(
    c =>
      (c.inputReports || []).some(r => r.reportId === reportId) ||
      (c.outputReports || []).some(r => r.reportId === reportId)
  )
}

async function resolveSbtDevice(hidDevice: HIDDevice | null): Promise<HIDDevice> {
  try {
    const existingDevices = await navigator.hid.getDevices()
    for (const d of existingDevices) {
      if (d.opened) {
        try {
          await d.close()
        } catch {
          /* ignore */
        }
      }
    }
  } catch {
    /* ignore */
  }

  try {
    const authorizedDevices = await navigator.hid.getDevices()
    const suitableDevice = authorizedDevices.find(d => hasReportId(d, 49)) || authorizedDevices[0]
    if (suitableDevice) {
      if (!suitableDevice.opened) await suitableDevice.open()
      ioDevices = [suitableDevice]
      return suitableDevice
    }
  } catch {
    /* ignore */
  }

  if (hidDevice) {
    if (!hidDevice.opened) await hidDevice.open()
    ioDevices = [hidDevice]
    return hidDevice
  }

  const freshDevices = await navigator.hid.requestDevice({ filters: [] })
  const picked = freshDevices[0]
  if (!picked) throw new Error('No device selected')
  if (!picked.opened) await picked.open()
  ioDevices = [picked]
  return picked
}

export async function startSbtOtaUpgrade(
  hidDevice: HIDDevice,
  firmware: Uint8Array,
  callbacks: UpgradeCallbacks = {},
  target = 0,
  userConfig: Partial<SbtConfig> = {},
  enterUpgradeCmd: Uint8Array | null = null
): Promise<void> {
  if (!firmware.length) throw new Error('Firmware data is empty')

  device = await resolveSbtDevice(hidDevice)
  firmwareBuffer = firmware
  upgradeTarget = target
  config = { ...DEFAULT_CONFIG, ...(userConfig || {}) }
  isRunning = true
  lastReportedProgress = 0
  useReportId49 = true
  if (ioDevices.length === 0 && device) ioDevices = [device]

  onProgressCallback = callbacks.onProgress ?? null
  onCompleteCallback = callbacks.onComplete ?? null
  onErrorCallback = callbacks.onError ?? null
  onLogCallback = callbacks.onLog ?? null

  isBluetoothUpgrade = !!(enterUpgradeCmd && enterUpgradeCmd.length >= 4 && enterUpgradeCmd[3] === 0x86)

  try {
    inputReportHandler = onInputReport
    for (const d of ioDevices) {
      d.addEventListener('inputreport', inputReportHandler)
    }

    if (enterUpgradeCmd && enterUpgradeCmd.length > 0) {
      await device.sendReport(49, enterUpgradeCmd)
    }

    await performUpgrade()
  } catch (error) {
    try {
      await sendUpdateFailedNotice(inferUpdateFailedReasonByte(error))
    } catch {
      /* best effort */
    }
    cleanup()
    const err = error instanceof Error ? error : new Error(String(error))
    onErrorCallback?.(err)
    throw err
  } finally {
    cleanup()
  }
}

export function cancelSbtOtaUpgrade(): void {
  isRunning = false
  cleanup()
}
